import { useLaunchDarkly } from '@/components/App/LaunchDarklyGateway';
import {
  FEATURE_FLAG_CONFIG, type FeatureFlags,
} from '@/hooks/featureFlag/featureFlagConfig';

export type { FeatureFlags } from '@/hooks/featureFlag/featureFlagConfig';

/**
 * Centralized hook for accessing feature flags throughout the application.
 *
 * This hook provides a typed abstraction layer over LaunchDarkly's useFlags hook,
 * allowing for centralized flag identifier management and easier maintenance.
 *
 * @returns Object containing all feature flags with their current values
 *
 * @example
 * const { isProductsManagementEnabled, isDistributorManagementEnabled } = useFeatureFlags();
 * if (isProductsManagementEnabled) {
 *   // Show products management UI
 * }
 */
export const useFeatureFlags = (): FeatureFlags => {
  const {
    flags: ldFlags,
    isClientInitialized,
  } = useLaunchDarkly();

  return Object.fromEntries(
    FEATURE_FLAG_CONFIG.map((flag) => {
      const value = isClientInitialized ? ldFlags[flag.externalKey] : undefined;
      const isValid = typeof value === typeof flag.fallback
        && (typeof value !== 'number' || Number.isFinite(value));

      return [flag.key, isValid ? value : flag.fallback];
    }),
  ) as FeatureFlags;
};
